import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useDocumentTitle } from '../../../hooks/use-document-title';
import { usePortalLiveRefresh } from '../../../hooks/use-portal-live-refresh';
import { BackButton } from '../../../layout/back-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { PassEditorModal } from '../../../pass-editor/pass-editor';
import { readPassError } from '../../../../utils/pass-errors';
import { FormAlert } from '../../../pass-editor/pass-editor.styled';
import {
  ListSkeleton,
  LoadError,
} from '../../dashboard/dashboard-shared-components/load-state';
import { listItemMotion, Staleable, StateSwitch, StatusLabel } from '../../../../motion';
import { getPass, passAction, updatePass } from '../../../../api/tenant-passes-client';
import {
  PASS_CATEGORY_LABELS,
  PassAction,
  PassOccurrence,
  ResidentPass,
  ResidentPassDetail,
  ResidentPassVisit,
} from '../../../../interfaces/visitor-pass';
import toast from '../../../../utils/toast';
import { describeWindow, formatVisitDay } from '../../../../utils/pass-schedule';
import { LIVE_VISITOR_EVENTS, readFailure, usePanel, whenAt, zoneNote } from '../portal-common';
import {
  Card,
  EmptyState,
  ListRow,
  PageNote,
  RowMain,
  RowMeta,
  RowTitle,
  Section,
  SectionHead,
  SectionTitle,
  TabTitle,
} from '../portal-kit.styled';
import {
  PASS_STATE_PILL,
  buildingToday,
  scheduleLine,
  visitWhen,
  visitorName,
} from './pass-card';
import { PassShare } from './pass-share';
import {
  ActionsNote,
  DetailActions,
  DetailFacts,
  DetailNote,
  DetailPills,
  DetailTitle,
  DetailTop,
  InlinePills,
  VisitMain,
  VisitRow,
  VisitSide,
} from './portal-visitors.styled';

/**
 * ONE PASS — /portal/visitors/:passId (resident-portal Plan 3 RP-3.4, Plan 4
 * RP-4.5).
 *
 * A nested route inside the portal shell, so the shell's one ROUTE_PERMISSIONS
 * key gates it and the Visitors tab stays lit. Back goes to the list by name
 * ("Back to visitors"), never a history offset: a resident who arrived from a
 * notification has no history to go back through.
 *
 * What the household sees: the schedule, the next eight visits in the
 * building's clock, and every time the desk signed this visitor in on the pass
 * — when, for how long, how many, and whether it was early or late. No staff
 * names, no other units.
 *
 * What any household member may do (RP-D3 — a pass the building added too):
 * skip one visit or undo the skip, pause and resume, edit, and end. A building
 * that has switched passes off refuses everything but End, so that is all the
 * page offers then (the unit screen's rule, unit-passes.tsx). Every action
 * answers with the pass as it now stands and the page redraws from it; a
 * refusal is a sentence of ours above the actions, never the server's text.
 */

/* A pass that is not ours, or not there any more, is a 404 from the server —
   the same answer for both, and a sentence here rather than a failure. */
const loadOrMissing = async (passId: string): Promise<ResidentPassDetail | null> => {
  try {
    return await getPass(passId);
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) return null;
    throw error;
  }
};

const visitLine = (visit: ResidentPassVisit, tz?: string): string => {
  const parts = [whenAt(visit.arrived_at, tz)];
  if (visit.departed_at) parts.push(`left ${whenAt(visit.departed_at, tz)}`);
  else parts.push('still inside');
  if (visit.party_size > 1) parts.push(`party of ${visit.party_size}`);

  return parts.filter(Boolean).join(' · ');
};

const WINDOW_PILL: Record<string, string> = { early: 'Early', late: 'Late' };

/** How many upcoming rows the page lists — the server's detail count. */
const VISIT_ROWS = 8;

export const PASS_GONE = 'This visitor pass is not here any more. It may have ended.';
export const PASS_PENDING_NOTE =
  "Your building approves visitor passes. Nobody is expected until they do, and you'll be told. You can change it or end it meanwhile.";
export const PASSES_OFF_DETAIL_NOTE =
  "Your building has switched visitor passes off, so it can't be changed. Ending it still works.";

/* One row under "Next visits": a visit still on, or a date the household
   skipped (kept in the list, in date order, so it can be undone). */
interface VisitRowModel {
  date: string;
  occurrence: PassOccurrence | null;
}

export const upcomingRows = (pass: ResidentPass, today: string): VisitRowModel[] => {
  if (pass.state === 'ended') return [];
  const onDates = new Set(pass.next.map(occurrence => occurrence.local_date));
  const skipped = Array.from(new Set(pass.skip_dates || []))
    .filter(date => date >= today && !onDates.has(date))
    .map(date => ({ date, occurrence: null }));

  return [...pass.next.map(occurrence => ({ date: occurrence.local_date, occurrence })), ...skipped]
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .slice(0, VISIT_ROWS);
};

const firstNameOf = (pass: ResidentPass): string => (pass.first_name || '').trim() || visitorName(pass);

const possessive = (name: string): string => `${name}'s`;

/* What each action says when it lands, and when it does not. */
const OUTCOME: Record<PassAction, (first: string, day: string) => string> = {
  pause: first => `${possessive(first)} pass is paused.`,
  resume: first => `${possessive(first)} pass is active again.`,
  revoke: first => `${possessive(first)} pass has ended.`,
  skip: (_first, day) => `Skipped ${day}.`,
  unskip: (_first, day) => `${day} is back on.`,
};

const FAILED: Record<PassAction, (first: string, day: string) => string> = {
  pause: first => `Could not pause ${possessive(first)} pass. Try again.`,
  resume: first => `Could not resume ${possessive(first)} pass. Try again.`,
  revoke: first => `Could not end ${possessive(first)} pass. Try again.`,
  skip: (_first, day) => `Could not skip ${day}. Try again.`,
  unskip: (_first, day) => `Could not put ${day} back on. Try again.`,
};

type Busy = { action: PassAction; date?: string } | null;

export const PortalPassDetail: React.FC = () => {
  const { passId = '' } = useParams<{ passId: string }>();
  const load = useCallback(() => loadOrMissing(passId), [passId]);
  const panel = usePanel(load);
  usePortalLiveRefresh(LIVE_VISITOR_EVENTS, panel.reload);

  // The pass as the last action answered it. Shown over the read until a
  // read at least as new arrives (a live refresh, a reload), so the page
  // never steps back to the state before the action.
  const [fresh, setFresh] = useState<ResidentPass | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const busyRef = useRef(false);
  const [actionError, setActionError] = useState('');
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const justEnded = useRef(false);

  const detail = panel.data;
  const base = detail?.pass;
  // Strictly newer: a read as new as the action's answer carries the same pass
  // AND what an action does not move updated_at for (Plan 6's `shared`).
  const pass =
    base && fresh && fresh.pass_id === base.pass_id && fresh.updated_at > base.updated_at
      ? fresh
      : base;
  const tz = pass?.building_timezone;
  const today = buildingToday(tz);
  const name = pass ? visitorName(pass) : '';
  useDocumentTitle(name ? `${name} — visitor pass` : 'Visitor pass');

  const rows = useMemo(() => (pass ? upcomingRows(pass, today) : []), [pass, today]);

  // Ended from the dialog: its End button has gone, so once the dialog has
  // closed the reader is put on the page's heading rather than on nothing.
  useEffect(() => {
    if (!confirmingEnd && justEnded.current && pass?.state === 'ended') {
      justEnded.current = false;
      titleRef.current?.focus();
    }
  }, [confirmingEnd, pass?.state]);

  const view =
    panel.status === 'loading' ? 'loading' : panel.status === 'error' ? 'error' : pass ? 'ready' : 'empty';

  /* EVERY ACTION, ONE AT A TIME. The ref is the double-tap guard (state
     arrives a render late); the outcome is toasted in our words, a refusal is
     said on the page. */
  const run = async (action: PassAction, date?: string): Promise<void> => {
    if (!pass || busyRef.current) return;
    busyRef.current = true;
    setBusy({ action, date });
    setActionError('');
    const first = firstNameOf(pass);
    const day = date ? formatVisitDay(date, today) : '';
    try {
      const next = await passAction(pass.pass_id, action, date);
      if (next && next.pass_id) setFresh(next);
      else panel.reload();
      if (action === 'revoke') justEnded.current = true;
      toast.success(OUTCOME[action](first, day));
    } catch (error) {
      const fallback = FAILED[action](first, day);
      const reading = readPassError(error, {
        limits: detail?.limits,
        fallback,
        offline: `${fallback.replace(/ Try again\.$/, '')} Check your connection and try again.`,
        notFound: PASS_GONE,
      });
      setActionError(reading.status === 400 ? fallback : reading.message);
      // Whatever was refused, the page shows the pass as it now is — a 404
      // (ended on another phone) lands on "not here any more".
      panel.reload();
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const openEditor = () => {
    setEditorKey(n => n + 1);
    setEditing(true);
  };

  const ended = pass?.state === 'ended';
  const off = pass?.state === 'off';
  const pending = pass?.state === 'pending';
  // Skip, pause / resume and edit: a live pass at a building that takes them.
  // Waiting for approval (125): edit and end only — the server refuses a pause.
  const changeable = !!pass && !ended && !off && !pending;
  const paused = pass?.state === 'paused';
  const first = pass ? firstNameOf(pass) : '';
  const pill = (pass && PASS_STATE_PILL[pass.state]) || PASS_STATE_PILL.active;
  const pausing = busy?.action === 'pause' || busy?.action === 'resume';

  return (
    <>
      <DetailTop>
        <BackButton to="/portal/visitors" label="Back to visitors" />
      </DetailTop>

      <StateSwitch state={view} minHeight={360}>
        {view === 'loading' ? (
          <>
            <TabTitle>Visitor pass</TabTitle>
            <ListSkeleton rows={4} label="the visitor pass" />
          </>
        ) : view === 'error' ? (
          <>
            <TabTitle>Visitor pass</TabTitle>
            <LoadError
              message={readFailure(panel.errorStatus, 'We could not load this visitor pass just now.')}
              onRetry={panel.reload}
            />
          </>
        ) : view === 'empty' || !pass ? (
          <>
            <TabTitle>Visitor pass</TabTitle>
            <Card>
              <EmptyState>{PASS_GONE}</EmptyState>
            </Card>
          </>
        ) : (
          <Staleable stale={panel.refreshing}>
            {panel.failed ? (
              <LoadError
                message={readFailure(
                  panel.errorStatus,
                  'We could not refresh this visitor pass just now. What is shown may be out of date.'
                )}
                onRetry={panel.reload}
              />
            ) : null}
            <DetailTitle ref={titleRef} tabIndex={-1}>
              {name}
            </DetailTitle>
            <DetailPills>
              <StatusPill tone="neutral">{PASS_CATEGORY_LABELS[pass.category] || 'Visitor'}</StatusPill>
              <StatusPill tone={pill.tone} dot>
                {pill.label}
              </StatusPill>
            </DetailPills>
            <DetailFacts>
              <span>{scheduleLine(pass, today)}</span>
              {pass.unit_label ? <span>For unit {pass.unit_label}</span> : null}
              {pass.max_party > 1 ? <span>Up to {pass.max_party} people</span> : null}
              {pass.created_via === 'staff' ? <span>Added by your building</span> : null}
              {pass.note ? <DetailNote>Note for the front desk: {pass.note}</DetailNote> : null}
              {pass.decision === 'rejected' ? (
                <DetailNote>
                  Declined by your building{pass.decision_note ? `: ${pass.decision_note}` : '.'}
                </DetailNote>
              ) : null}
            </DetailFacts>

            <FormAlert role="alert" message={actionError || undefined} />

            {ended ? null : (
              <DetailActions>
                {changeable ? (
                  <>
                    <AppButton
                      variant="secondary"
                      disabled={!!busy}
                      onClick={() => run(paused ? 'resume' : 'pause')}
                    >
                      <StatusLabel
                        state={pausing ? (paused ? 'resuming' : 'pausing') : paused ? 'resume' : 'pause'}
                        labels={{
                          pause: 'Pause pass',
                          resume: 'Resume pass',
                          pausing: 'Pausing…',
                          resuming: 'Resuming…',
                        }}
                      />
                    </AppButton>
                    <AppButton variant="secondary" disabled={!!busy} onClick={openEditor}>
                      Edit pass
                    </AppButton>
                  </>
                ) : null}
                {pending ? (
                  <AppButton variant="secondary" disabled={!!busy} onClick={openEditor}>
                    Edit pass
                  </AppButton>
                ) : null}
                <AppButton
                  variant="destructive"
                  disabled={!!busy}
                  onClick={() => setConfirmingEnd(true)}
                >
                  End pass
                </AppButton>
                {off ? <ActionsNote role="note">{PASSES_OFF_DETAIL_NOTE}</ActionsNote> : null}
                {pending ? <ActionsNote role="note">{PASS_PENDING_NOTE}</ActionsNote> : null}
              </DetailActions>
            )}

            {/* Plan 6: share the pass with the guest as a link. Renders nothing
                for a pass that can't be shared and isn't. */}
            {ended ? null : (
              <PassShare
                pass={pass}
                first={first}
                // Re-read, not setFresh: sharing does not move updated_at, so a
                // fresh copy would outrank a later read that saw a stop made
                // on another phone.
                onChanged={() => panel.reload()}
              />
            )}

            <Section>
              <SectionHead>
                <SectionTitle>Next visits</SectionTitle>
              </SectionHead>
              <Card>
                {rows.length === 0 ? (
                  <EmptyState>
                    {ended
                      ? 'This pass has ended.'
                      : pending
                      ? 'No visits until your building approves this pass.'
                      : 'No visits are coming up on this pass.'}
                  </EmptyState>
                ) : (
                  rows.map((row, index) => {
                    const day = formatVisitDay(row.date, today);
                    const skipped = !row.occurrence;
                    const rowBusy =
                      busy?.date === row.date && (busy.action === 'skip' || busy.action === 'unskip');

                    return (
                      /* Keyed by DATE, so a visit and its skipped self are the
                         same row and the button's label swaps in place. */
                      <VisitRow key={`visit-${row.date}`} {...listItemMotion(index)} layout={false}>
                        <VisitMain>
                          <RowTitle>
                            {row.occurrence
                              ? visitWhen(row.occurrence, pass, today)
                              : `${day}, ${describeWindow(pass.window_start, pass.window_end)}`}
                          </RowTitle>
                        </VisitMain>
                        {skipped || changeable ? (
                          <VisitSide>
                            {skipped ? <StatusPill tone="neutral">Skipped</StatusPill> : null}
                            {changeable ? (
                              <AppButton
                                variant="ghost"
                                size="compact"
                                aria-label={skipped ? `Undo skip ${day}` : `Skip ${day}`}
                                disabled={!!busy}
                                onClick={() => run(skipped ? 'unskip' : 'skip', row.date)}
                              >
                                <StatusLabel
                                  state={
                                    rowBusy ? (skipped ? 'undoing' : 'skipping') : skipped ? 'undo' : 'skip'
                                  }
                                  labels={{
                                    skip: 'Skip',
                                    undo: 'Undo skip',
                                    skipping: 'Skipping…',
                                    undoing: 'Undoing…',
                                  }}
                                />
                              </AppButton>
                            ) : null}
                          </VisitSide>
                        ) : null}
                      </VisitRow>
                    );
                  })
                )}
              </Card>
            </Section>

            <Section>
              <SectionHead>
                <SectionTitle>Visit history</SectionTitle>
              </SectionHead>
              <Card>
                {detail && detail.visits.length ? (
                  detail.visits.map((visit, index) => (
                    <ListRow key={`${visit.arrived_at}-${index}`} {...listItemMotion(index)} layout={false}>
                      <RowMain>
                        <RowTitle>{visitLine(visit, tz)}</RowTitle>
                        {visit.window_state && WINDOW_PILL[visit.window_state] ? (
                          <RowMeta>
                            <InlinePills>
                              <StatusPill tone="warning">{WINDOW_PILL[visit.window_state]}</StatusPill>
                            </InlinePills>
                          </RowMeta>
                        ) : null}
                      </RowMain>
                    </ListRow>
                  ))
                ) : (
                  <EmptyState>Nobody has been signed in on this pass yet.</EmptyState>
                )}
              </Card>
              <PageNote data-clock-note="" data-clock-whose="building">
                {zoneNote(tz)}
              </PageNote>
            </Section>
          </Staleable>
        )}
      </StateSwitch>

      {(changeable || pending) && pass && detail ? (
        <PassEditorModal<ResidentPass>
          key={editorKey}
          open={editing}
          onClose={() => setEditing(false)}
          mode="resident"
          limits={detail.limits}
          today={today}
          zone={tz || 'UTC'}
          initial={pass}
          onSubmit={spec => updatePass(pass.pass_id, spec)}
          onSaved={saved => {
            setEditing(false);
            if (saved && saved.pass_id) setFresh(saved);
            panel.reload();
          }}
        />
      ) : null}

      {/* The safe option — "Keep pass" — takes the focus: ConfirmDialog puts
          data-autofocus on its dismiss, so a double Enter keeps the pass. */}
      <ConfirmDialog
        open={confirmingEnd}
        title={`End ${possessive(first)} pass?`}
        message={`${first} will no longer appear on the front desk's expected list.`}
        primaryLabel="End pass"
        primaryVariant="destructive"
        dismissLabel="Keep pass"
        onPrimary={async () => {
          await run('revoke');
          setConfirmingEnd(false);
        }}
        onDismiss={() => setConfirmingEnd(false)}
      />
    </>
  );
};
